import { RefObject, useEffect } from 'react';
import { HTMLElementWithAnchor } from './Popover2025';

type UseAnchorDescribedByArgs = {
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  descriptionId: string;
  isActive: boolean;
};

/**
 * While active, links the `anchorRef` element to a description element via
 * `aria-describedby` so assistive tech announces it when the anchor is focused.
 * Composes with any existing `aria-describedby` and restores it on cleanup.
 */
export const useAnchorDescribedBy = ({
  anchorRef,
  descriptionId,
  isActive,
}: UseAnchorDescribedByArgs): void => {
  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !isActive) return;
    const previous = anchor.getAttribute('aria-describedby');
    anchor.setAttribute(
      'aria-describedby',
      previous ? `${previous} ${descriptionId}` : descriptionId
    );
    return () => {
      const remaining = (anchor.getAttribute('aria-describedby') ?? '')
        .split(' ')
        .filter(token => token && token !== descriptionId);
      if (remaining.length > 0) {
        anchor.setAttribute('aria-describedby', remaining.join(' '));
      } else {
        anchor.removeAttribute('aria-describedby');
      }
    };
  }, [anchorRef, isActive, descriptionId]);
};
